import { action } from "@ember/object";
import type { ForcBaseArgs } from "parog-web/components/forc/abstract/base-component";
import CrudJournalEventBaseComponent from "parog-web/components/forc/abstract/crud-journal-event-based-component";
import journalEventRetrievalStrategies from "parog-web/components/forc/utils/journal-event-retrieval-strategies";
import type Patient from "parog-web/models/patient";

interface Signature {
  Args: {
    patient: Patient;
  } & ForcBaseArgs;
}

export default abstract class CtcaeCustomBaseConsultFormEditionForm extends CrudJournalEventBaseComponent<Signature> {
  retrieveJournalEvent = journalEventRetrievalStrategies.sourceJournalEvent;

  get currentSections(): Array<string> {
    return [];
  }

  @action
  async save(): Promise<typeof this.journalEventClass> {
    this.changeset.changes.forEach(change => {
      if (!this.currentSections.includes(change.key)) {
        this.changeset.rollbackProperty(change.key);
      }
    });
    return super.save();
  }

  get temperatureSections(): Array<string> {
    if (this.changeset.get("temperatureIssue")) {
      return ["temperatureIssue", "temperature"];
    }
    return ["temperatureIssue"];
  }

  get fatigueSections(): Array<string> {
    if (this.changeset.get("fatigueIssue")) {
      return ["fatigueIssue", "fatigue"];
    }
    return ["fatigueIssue"];
  }

  get coughSections(): Array<string> {
    if (this.changeset.get("coughIssue")) {
      return ["coughIssue", "cough"];
    }
    return ["coughIssue"];
  }

  get breathSections(): Array<string> {
    if (this.changeset.get("breathIssue")) {
      return ["breathIssue", "breath"];
    }
    return ["breathIssue"];
  }

  get weightLossSections(): Array<string> {
    if (this.changeset.get("weightLossIssue")) {
      return ["weightLossIssue", "weightLoss"];
    }
    return ["weightLossIssue"];
  }

  get dermatologicalSections(): Array<string> {
    if (this.changeset.get("dermatologicalIssue")) {
      return [
        "dermatologicalIssue",
        ...this.skinRashSections,
        ...this.itchingSections,
        "dermatologicalTreatmentEfficient"
      ];
    }
    return ["dermatologicalIssue"];
  }

  get skinRashSections(): Array<string> {
    if (this.changeset.get("skinRashIssue")) {
      return ["skinRashIssue", "skinRashLocation"];
    }
    return ["skinRashIssue"];
  }

  get itchingSections(): Array<string> {
    if (this.changeset.get("itchingIssue")) {
      return ["itchingIssue", "itchingLocation"];
    }
    return ["itchingIssue"];
  }

  get nauseaVomitingSections(): Array<string> {
    if (this.changeset.get("nauseaVomitingIssue")) {
      return ["nauseaVomitingIssue", "nauseaVomiting"];
    }
    return ["nauseaVomitingIssue"];
  }

  get diarrheaSections(): Array<string> {
    if (this.changeset.get("diarrheaIssue")) {
      return ["diarrheaIssue", "diarrhea"];
    }
    return ["diarrheaIssue"];
  }

  get constipationSections(): Array<string> {
    if (this.changeset.get("constipationIssue")) {
      return [
        "constipationIssue",
        "constipation",
        "constipationTreatmentEfficient"
      ];
    }
    return ["constipationIssue"];
  }

  get painSections(): Array<string> {
    if (this.changeset.get("painIssue")) {
      return ["painIssue", "pain"];
    }
    return ["painIssue"];
  }

  get muscularPainSections(): Array<string> {
    if (this.changeset.get("muscularPainIssue")) {
      return [
        "muscularPainIssue",
        "muscularPainLevel",
        "muscularPainLocation",
        "muscularPainTreatmentEfficient"
      ];
    }
    return ["muscularPainIssue"];
  }

  get articularPainSections(): Array<string> {
    if (this.changeset.get("articularPainIssue")) {
      return [
        "articularPainIssue",
        "articularPainLevel",
        "articularPainLocation",
        "articularPainTreatmentEfficient"
      ];
    }
    return ["articularPainIssue"];
  }

  get otherPainSections(): Array<string> {
    if (this.changeset.get("otherPainIssue")) {
      return [
        "otherPainIssue",
        "otherPainLocation",
        "otherPainTreatmentEfficient"
      ];
    }
    return ["otherPainIssue"];
  }

  get cortisoneSections(): Array<string> {
    if (this.changeset.get("cortisone")) {
      return ["cortisone", "cortisoneDosageGt20"];
    }
    return ["cortisone"];
  }
}
